/**
 * `useSessionInit` — creates a funnel session via `POST /api/funnel/sessions`
 * once the funnel config is available from the application context.
 *
 * Returns the sessionId needed by the engine. Loading state gates the
 * engine from starting until the session row exists in D1.
 *
 * NX-10230: On page reload, browser-Back, or auth round-trip the hook
 * reuses the session ID persisted in localStorage by `sessionPersistence`
 * instead of creating a duplicate D1 row. This keeps experiment session
 * counts accurate (one session per funnel visit, not per page load).
 *
 * NX-12391: When the session store is unavailable the funnel is still served.
 * A visitor who cannot be recorded is worth more than an error page, so the
 * hook mints a local `sess_degraded_*` id and lets the engine start. Analytics
 * events still reach WAE and the R2 archive (neither depends on D1), and the
 * prefix keeps these sessions identifiable as having no session row rather than
 * silently indistinguishable from recorded ones. What is genuinely lost is the
 * inline attribution envelope, so ad-network conversions for these visitors
 * cannot be attributed.
 */
import { useEffect, useMemo, useState } from "react";

import { readAttributionContext } from "@nextday/attribution";
import type { FunnelConfig } from "@nextday/funnel-engine";

import { createSession } from "@src/funnel/config/resolveFunnelConfig";
import { flattenAttribution } from "@src/funnel/flattenAttribution";
import { readPersistedSession, writePersistedSession } from "@src/funnel/sessionPersistence";

export type SessionInitState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; sessionId: string; degraded?: true };

const DEGRADED_SESSION_PREFIX = "sess_degraded_";

export const isDegradedSessionId = (sessionId: string): boolean =>
  sessionId.startsWith(DEGRADED_SESSION_PREFIX);

const buildDegradedSessionId = (): string => {
  const uuid =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now().toString(16)}${Math.random().toString(16).slice(2)}`;
  return `${DEGRADED_SESSION_PREFIX}${uuid.replaceAll("-", "")}`;
};

export type UseSessionInitArgs = {
  applicationId: string;
  anonymousId: string;
  config: FunnelConfig;
  experimentId?: string;
  /** NX-9949: Experiment arm id assigned via bucketing. */
  armId?: string;
  /** NX-9847: Published funnel version number. */
  funnelVersion?: number;
  /** Resolved inlet D1 row id — stamped on the attribution archive for WAE events. */
  inletId?: string;
  /** NX-10213: True when resolved in preview/draft mode. */
  isPreview?: boolean;
  /**
   * NX-12391: The config was served from a snapshot because the session store
   * is unavailable, so the create call is known to fail. Skipping it saves the
   * visitor the retry budget (~3.5s) and spares the failing store the load.
   */
  sessionStoreUnavailable?: boolean;
};

export const useSessionInit = (args: UseSessionInitArgs): SessionInitState => {
  const [state, setState] = useState<SessionInitState>({ status: "loading" });
  const attribution = useMemo(() => flattenAttribution(readAttributionContext()), []);

  useEffect(() => {
    // Reuse a persisted session from a prior page load (Back button,
    // refresh, auth round-trip) so we don't create a duplicate D1 row.
    const persisted = readPersistedSession(
      args.config.id,
      args.config.overrideId,
      args.anonymousId,
    );
    if (persisted) {
      setState({
        status: "ready",
        sessionId: persisted,
        ...(isDegradedSessionId(persisted) ? { degraded: true as const } : {}),
      });
      return undefined;
    }

    if (args.sessionStoreUnavailable) {
      setState({ status: "ready", sessionId: buildDegradedSessionId(), degraded: true });
      return undefined;
    }

    const controller = new AbortController();
    let cancelled = false;
    setState({ status: "loading" });

    const init = async (): Promise<void> => {
      try {
        const mergedAttribution =
          args.inletId && attribution
            ? { ...attribution, inlet_id: args.inletId }
            : args.inletId
              ? { inlet_id: args.inletId }
              : attribution;
        const session = await createSession({
          applicationId: args.applicationId,
          anonymousId: args.anonymousId,
          funnelId: args.config.id,
          overrideId: args.config.overrideId,
          ...(mergedAttribution ? { attribution: mergedAttribution } : {}),
          ...(args.experimentId ? { experimentId: args.experimentId } : {}),
          ...(args.armId ? { armId: args.armId } : {}),
          ...(args.funnelVersion !== undefined ? { funnelVersion: args.funnelVersion } : {}),
          ...(args.isPreview ? { isPreview: true } : {}),
          signal: controller.signal,
        });
        if (!cancelled) {
          writePersistedSession(
            args.config.id,
            args.config.overrideId,
            session.session_id,
            args.anonymousId,
          );
          setState({ status: "ready", sessionId: session.session_id });
        }
      } catch (error) {
        if (cancelled) return;
        if ((error as Error).name === "AbortError") return;
        // Serve the funnel unrecorded rather than showing an error. The id is
        // deliberately not persisted: a persisted degraded id would be reused
        // for a week and would keep a visitor unrecorded long after the store
        // recovered, which costs more than the duplicate ids a refresh during
        // the outage produces.
        setState({ status: "ready", sessionId: buildDegradedSessionId(), degraded: true });
      }
    };

    void init();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    args.applicationId,
    args.anonymousId,
    args.config.id,
    args.config.overrideId,
    args.experimentId,
    args.armId,
    args.funnelVersion,
    args.inletId,
    args.isPreview,
    args.sessionStoreUnavailable,
  ]);

  return state;
};
